/**
 * Parametric "anime background" colour grade, keyed by sun altitude and baked into a 3D LUT (display-referred,
 * applied after AgX). Pure: no three.js. Numbers are phase-2 starting points, tuned at checkpoint K2.
 */
import type { RGB } from './skyPalette';

export interface Grade {
  lift: RGB;
  gamma: RGB;
  gain: RGB;
  saturation: number;
  shadowTint: RGB;
  highlightTint: RGB;
  /** 0..1 strength of the split tint. */
  tint: number;
}

export const IDENTITY_GRADE: Grade = {
  lift: [0, 0, 0],
  gamma: [1, 1, 1],
  gain: [1, 1, 1],
  saturation: 1,
  shadowTint: [1, 1, 1],
  highlightTint: [1, 1, 1],
  tint: 0,
};

const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));
const luma = (c: RGB): number => 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];

export function applyGrade(c: RGB, g: Grade): RGB {
  const graded = [0, 1, 2].map((i) => {
    const x = Math.max(0, c[i]! * g.gain[i]! + g.lift[i]! * (1 - c[i]!));
    return Math.pow(x, 1 / g.gamma[i]!);
  }) as RGB;
  const l = luma(graded);
  return [0, 1, 2].map((i) => {
    const sat = l + (graded[i]! - l) * g.saturation;
    const shadow = 1 + (g.shadowTint[i]! - 1) * (1 - l) * g.tint;
    const high = 1 + (g.highlightTint[i]! - 1) * l * g.tint;
    return clamp01(sat * shadow * high);
  }) as RGB;
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const lerp3 = (a: RGB, b: RGB, t: number): RGB => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];

export function lerpGrade(a: Grade, b: Grade, t: number): Grade {
  return {
    lift: lerp3(a.lift, b.lift, t),
    gamma: lerp3(a.gamma, b.gamma, t),
    gain: lerp3(a.gain, b.gain, t),
    saturation: lerp(a.saturation, b.saturation, t),
    shadowTint: lerp3(a.shadowTint, b.shadowTint, t),
    highlightTint: lerp3(a.highlightTint, b.highlightTint, t),
    tint: lerp(a.tint, b.tint, t),
  };
}

/** Starting keyframes (K2 tunes them): cool blue shadows, warm highlights, saturation that stays below candy. */
export const GRADE_KEYS: Array<[number, Grade]> = [
  [-12, { ...IDENTITY_GRADE, lift: [0.005, 0.012, 0.04], gain: [0.95, 0.97, 1.02], saturation: 1.1, shadowTint: [0.75, 0.88, 1.25], highlightTint: [1.12, 1.0, 0.85], tint: 0.55 }],
  [-5, { ...IDENTITY_GRADE, lift: [0.0, 0.012, 0.05], gain: [1.02, 0.98, 1.0], saturation: 1.25, shadowTint: [0.72, 0.85, 1.3], highlightTint: [1.15, 1.02, 0.88], tint: 0.6 }],
  [4, { ...IDENTITY_GRADE, lift: [0.0, 0.0, 0.025], gamma: [0.95, 0.95, 0.95], gain: [1.06, 0.98, 0.9], saturation: 1.35, shadowTint: [0.85, 0.88, 1.2], highlightTint: [1.15, 1.03, 0.85], tint: 0.55 }],
  [20, { ...IDENTITY_GRADE, lift: [0, 0.005, 0.015], gain: [1, 1, 1.02], saturation: 1.12, shadowTint: [0.92, 0.97, 1.1], highlightTint: [1.04, 1.02, 0.97], tint: 0.35 }],
];

export function gradeForSunAlt(sunAltDeg: number, keys: Array<[number, Grade]> = GRADE_KEYS): Grade {
  const first = keys[0]!;
  const last = keys[keys.length - 1]!;
  if (!(sunAltDeg > first[0])) return first[1];
  if (sunAltDeg >= last[0]) return last[1];
  let i = 0;
  while (sunAltDeg > keys[i + 1]![0]) i++;
  const [a0, ga] = keys[i]!;
  const [a1, gb] = keys[i + 1]!;
  return lerpGrade(ga, gb, (sunAltDeg - a0) / (a1 - a0));
}

/** RGBA float LUT, red fastest: index = ((b * size + g) * size + r) * 4. */
export function buildLut3D(g: Grade, size = 32): Float32Array {
  const data = new Float32Array(size * size * size * 4);
  for (let b = 0; b < size; b++)
    for (let gg = 0; gg < size; gg++)
      for (let r = 0; r < size; r++) {
        const out = applyGrade([r / (size - 1), gg / (size - 1), b / (size - 1)], g);
        const i = ((b * size + gg) * size + r) * 4;
        data[i] = out[0];
        data[i + 1] = out[1];
        data[i + 2] = out[2];
        data[i + 3] = 1;
      }
  return data;
}
